import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { LabelTextGroup } from '@components/label-text-group';
import { Separator } from '@components/separator/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VariableValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate, addTimeToDate, getRelativeDate } from '@utils/date';
import { formatTime } from '@utils/strings';

import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';

import { ReferenceDate } from './journey-sidebar';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  ActionTimeSelector,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionSidebar,
  useEditingContext,
  WaitForDateStateGroup,
} from '../common';
import { getFieldMappingId } from '../helpers';
import { ActionTimeWithTimezoneTooltip, newDateIgnoreLocalTimezone } from '../timezone';

const ACTION_TYPE = ACTION_TYPES.wait_until_relative_date;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();

  const [offset] = action?.timezone?.split(' ') as string[];

  const { comparator, days, referenceDate } = action.data;
  const values = action.values;
  const variables = values?.referenceDate?.variables || {};
  const field = variables[Object.keys(variables)[0]]?.label ?? undefined;

  let scheduledDate: Date | null = null;
  let hour: string | undefined;
  let minute: string | undefined;

  if (referenceDate) {
    const time = formatTime(values?.hour?.value, values?.minute?.value);
    const relativeReferenceDate = getRelativeDate(referenceDate, comparator, Number(days));
    const dateWithTime = addTimeToDate(relativeReferenceDate, time);
    scheduledDate = newDateIgnoreLocalTimezone(dateWithTime);
    hour = scheduledDate?.getHours()?.toString();
    minute = scheduledDate?.getMinutes()?.toString();
  } else if (action.savedValues?.date) {
    scheduledDate = newDateIgnoreLocalTimezone(action?.savedValues?.date);
    hour = scheduledDate?.getHours()?.toString();
    minute = scheduledDate?.getMinutes()?.toString();
  }

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  const configText =
    comparator === 'lt'
      ? t('configLessThan', { days, field })
      : comparator === 'gt'
      ? t('configGreaterThan', { days, field })
      : t('configOnDate', { field });

  return (
    <Stack space={6}>
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        isCard={false}
        progressPercentage=" "
      />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <div>
            <Text font="semibold14" text={t('configuration')} />
            <Text font="regular14" text={t('configurationDescription')} />
            <Text font="regular14" color="elementBlue100" text={configText} />
          </div>
          <LabelTextGroup
            label={field}
            text={referenceDate ? abbreviateDate(referenceDate, 'en', 'date') : ''}
          />
          <div>
            <Text font="semibold14" text={t('triggersNextAction')} />
            <ActionTimeWithTimezoneTooltip
              actionText={
                referenceDate && scheduledDate
                  ? abbreviateDate(scheduledDate, 'en', 'datetime')
                  : ''
              }
              hour={hour ?? ''}
              minute={minute ?? ''}
              journeyTimezoneOffset={offset}
            />
          </div>
        </Stack>
      </HideContainer>
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type WaitForData = {
  daysBefore: string;
  daysAfter: string;
  hour: string;
  minute: string;
  referenceDate: VariableValue;
  dateOffset: DateOffsetTypes;
  description: string;
};

type DateOffsetTypes = 'on-date' | 'before-date' | 'after-date';

const Form = createForm<WaitForData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const dateOffset = formStore.useFieldValue('dateOffset') as DateOffsetTypes;
  const daysBefore = formStore.useFieldValue('daysBefore') as string;
  const daysAfter = formStore.useFieldValue('daysAfter') as string;
  const hour = formStore.useFieldValue('hour') as string;
  const minute = formStore.useFieldValue('minute') as string;
  const time = formatTime(hour, minute);
  const referenceDate = formStore.useFieldValue('referenceDate') as VariableValue;
  const variables = referenceDate?.variables || {};
  const field = variables[Object.keys(variables)[0]]?.label ?? t('referenceDate');
  const [offset] = action?.timezone?.split(' ') ?? [''];
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const values = action.values;
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['date'],
  });
  const handleSave = async (data: WaitForData) => {
    return await editMutation.mutate({
      values: {
        ...(data.daysBefore && { days: { value: data.daysBefore }, comparator: { value: 'lt' } }),
        ...(data.daysAfter && { days: { value: data.daysAfter }, comparator: { value: 'gt' } }),
        hour: { value: data.hour },
        minute: { value: data.minute },
        referenceDate: data.referenceDate,
      },
      description: data.description,
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <Stack space={4}>
          <WaitForDateStateGroup selectedStateId={action.action} />
          <Text font="regular14" text={t('selectBasedOnProperty')} />
        </Stack>
        <Separator />
        <Stack space={4}>
          <Form.FieldMappingField
            id="referenceDate"
            label={t('referenceDate')}
            plaintext
            singleLine
            fieldType="date"
            hideManualInput
            variableGroups={variableGroups}
            defaultValue={values?.referenceDate}
            validators={['required']}
            fieldMappingId={fieldMappingId}
          />
          <ReferenceDate values={action.values ?? {}} />
          {dateOffset === 'before-date' && (
            <Stack space={4}>
              <Form.TextField
                defaultValue={values?.days?.value}
                id="daysBefore"
                label={t('daysBefore')}
                validators={['required', 'number']}
              />
              <ActionTimeSelector
                defaultHour={values?.hour?.value}
                defaultMinute={values?.minute?.value}
              />
              <ActionTimeWithTimezoneTooltip
                actionText={t('beforeText', { daysBefore, time, field })}
                hour={hour}
                minute={minute}
                journeyTimezoneOffset={offset}
              />
            </Stack>
          )}
          {dateOffset === 'on-date' && (
            <Stack space={4}>
              <ActionTimeSelector
                defaultHour={values?.hour?.value}
                defaultMinute={values?.minute?.value}
              />
              <ActionTimeWithTimezoneTooltip
                actionText={t('onText', { time, field })}
                hour={hour}
                minute={minute}
                journeyTimezoneOffset={offset}
              />
            </Stack>
          )}
          {dateOffset === 'after-date' && (
            <Stack space={4}>
              <Form.TextField
                defaultValue={values?.days?.value}
                id="daysAfter"
                label={t('daysAfter')}
                validators={['number', 'required']}
              />
              <ActionTimeSelector
                defaultHour={values?.hour?.value}
                defaultMinute={values?.minute?.value}
              />
              <ActionTimeWithTimezoneTooltip
                actionText={t('afterText', { daysAfter, time, field })}
                hour={hour}
                minute={minute}
                journeyTimezoneOffset={offset}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    completed: 'Completed',
    error: 'Error',
    notStarted: 'Not started',
    inProgress: 'In progress',
    actionDetails: 'Action details',
    description: 'Description',
    configuration: 'Configuration',
    configurationDescription: 'The following action/s will be triggered at',
    configLessThan: '{days} Days • Before date • {field}',
    configOnDate: 'On date • {field}',
    configGreaterThan: '{days} Days • After date • {field}',
    triggersNextAction: 'Triggers next action',
    selectBasedOnProperty: 'Select a date based on a travellers journey property',
    referenceDate: 'Reference Date',
    daysBefore: 'Days before',
    daysAfter: 'Days after',
    beforeText: 'Triggers the following action {daysBefore} days before {field} at {time}',
    onText: 'Triggers the following action on {field} at {time}',
    afterText: 'Triggers the following action {daysAfter} days after {field} at {time}',
  },
};
